import {
    Button,
    IconButton as ChakraIconButton,
    Drawer,
    DrawerContent,
    DrawerOverlay,
    Flex,
    Image,
    Text,
    useColorMode,
    useTheme,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import React, { SetStateAction, useEffect, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { AiFillRead, AiOutlineFileSearch } from 'react-icons/ai';
import { BsQuestionCircleFill } from 'react-icons/bs';
import { FaChartLine } from 'react-icons/fa';
import { IoMdDocument } from 'react-icons/io';
import { RiFileExcel2Fill, RiListSettingsFill } from 'react-icons/ri';
import { SiTableau } from 'react-icons/si';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { RootState } from '../../../app-state/store';
import apiClient from '../../api';
import * as ga from '../../google-analytics';
import { useCurrentColors } from '../../hooks/UseCurrentColors/UseCurrentColors';
import { useCheckForMobileOrTablet } from '../../hooks/UseResponsive/UseResponsive';
import IconButton from '../IconButton/IconButton';
import Analytics from '../Icons/Analytics';
import NavUI from '../Icons/NavUI';
export interface SiderProps {
    isOpen?: boolean;
    setIsOpen: React.Dispatch<SetStateAction<boolean>>;
    menu: Array<any>;
    t: any;
}

const IconMap: Record<string, any> = {
    dashboard: 'NavDash',
    'client-list': 'NavClient',
    'client-list-base-ccy': 'NavClient',
    portfolio: 'NavPort',
    performance: 'NavPort',
    'customer-portfolio': 'NavPort',
    explorer: 'NavExp',
    reports: 'NavRep',
    'tableau-to-ppt': 'NavRep',
    'view-reports': 'NavRep',
    settings: 'NavSet',
    'excel-addin': <RiFileExcel2Fill />,
    resources: <AiFillRead />,
    'how-it-works': <BsQuestionCircleFill />,
    'tableau-insights': <SiTableau />,
    'partner-documents': <IoMdDocument />,
    'customer-documents': <IoMdDocument />,
    'tableau-analytics': <FaChartLine />,
    'analytics-mappings': <Analytics />,
    'menu-mappings': <RiListSettingsFill />,
    'review-all': <AiOutlineFileSearch />,
    'sidebar-upload-documents': <NavUI />,
};
const Sider: React.FC<SiderProps> = ({
    isOpen = false,
    setIsOpen,
    menu = [
        { title: 'Dashboard', path: 'dashboard', key: 'dashboard' },
        { title: 'Clients', path: 'clients', key: 'clients' },
        { title: 'Portfolio', path: 'portfolio', key: 'portfolio' },
        { title: 'Explorer', path: 'explorer', key: 'explorer' },
        {
            title: 'Reports',
            path: 'reports',
            key: 'reports',
            subMenu: [{ title: 'Generate Reports', path: 'reports' }],
        },
        { title: 'Settings', path: 'settings', key: 'settings' },
    ],
    t,
}: // setOpen,
SiderProps) => {
    const [open, setOpen] = useState<boolean>();
    const [subMenuOpenStatus, setSubMenuOpenStatus] = useState<Array<boolean>>(
        menu.map(() => false)
    );
    const currentColors = useCurrentColors();
    const { colorMode, toggleColorMode } = useColorMode();
    const { pathname } = useLocation();
    const isMobile = useCheckForMobileOrTablet();
    const toggleSubMenuDisplay = (index: number) => {
        const newSubMenuOpenStatus = [...subMenuOpenStatus];
        newSubMenuOpenStatus[index] = !newSubMenuOpenStatus[index];
        setSubMenuOpenStatus(newSubMenuOpenStatus);
    };
    const theme = useTheme();
    const {
        preference: { data },
    }: any = useSelector((state: RootState) => state.themeConfig);
    const logo = theme?.images?.headerLogo;
    const hasTheme = data?.colors?.light && data?.colors?.dark;
    useEffect(() => {
        setOpen(isOpen);
    }, [isOpen]);

    if (isMobile) {
        return (
            <Drawer
                isOpen={Boolean(open)}
                placement="left"
                onClose={() => {
                    setOpen(false);
                    setIsOpen(false);
                }}
            >
                <DrawerOverlay>
                    <DrawerContent
                        height={{ xl: '100vh', sm: 'calc(100% - 10px)' }}
                        bg={currentColors.menuBg}
                        width="210px"
                    >
                        <Flex
                            height="70px"
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Image
                                src={logo}
                                maxWidth="100%"
                                width="auto"
                                height={'35px'}
                                mx="auto"
                                mt="17.5px"
                                mb="17.5px"
                            />
                        </Flex>
                        {menu.map(({ title, subMenu, path, key }, index) => {
                            const isActive = pathname
                                ?.split('/')
                                ?.filter((item: any) => item)
                                .includes(
                                    path
                                        ?.split('/')
                                        ?.filter((item: any) => item)[0]
                                );
                            const IconName = IconMap[key] || 'NavDash';
                            return (
                                <Flex
                                    key={key}
                                    alignItems="center"
                                    ml="30px"
                                    justifyContent="center"
                                >
                                    {typeof IconName === 'string' ? (
                                        <IconButton
                                            cursor="pointer"
                                            border="none"
                                            width="20px"
                                            height="20px"
                                            backgroundColor="transparent"
                                            color={
                                                isActive
                                                    ? currentColors.primaryColor
                                                    : currentColors.menuIconColor
                                            }
                                            icon={IconName}
                                            aria-label={key}
                                            _hover={{
                                                backgroundColor: 'inherit',
                                                color: currentColors.brand[
                                                    '300'
                                                ],
                                                outline: 'none',
                                                boxShadow: 'none',
                                                border: '0px',
                                            }}
                                            _active={{
                                                backgroundColor: 'inherit',
                                                color: currentColors.primaryColor,
                                                outline: 'none',
                                                boxShadow: 'none',
                                                border: '0px',
                                            }}
                                            _focus={{
                                                backgroundColor: 'inherit',
                                                color: currentColors.primaryColor,
                                                outline: 'none',
                                                boxShadow: 'none',
                                                border: '0px',
                                            }}
                                        ></IconButton>
                                    ) : (
                                        <ChakraIconButton
                                            backgroundColor="transparent"
                                            cursor="pointer"
                                            border="none"
                                            minWidth="20px"
                                            width="20px"
                                            height="20px"
                                            color={
                                                isActive
                                                    ? currentColors.primaryColor
                                                    : currentColors.menuIconColor
                                            }
                                            icon={IconName}
                                            aria-label={key}
                                            _hover={{
                                                backgroundColor: 'inherit',
                                                color: currentColors.brand[
                                                    '300'
                                                ],
                                                outline: 'none',
                                                boxShadow: 'none',
                                                border: '0px',
                                            }}
                                            _active={{
                                                backgroundColor: 'inherit',
                                                color: currentColors.primaryColor,
                                                outline: 'none',
                                                boxShadow: 'none',
                                                border: '0px',
                                            }}
                                            _focus={{
                                                backgroundColor: 'inherit',
                                                color: currentColors.primaryColor,
                                                outline: 'none',
                                                boxShadow: 'none',
                                                border: '0px',
                                            }}
                                        ></ChakraIconButton>
                                    )}
                                    <Button
                                        background="transparent"
                                        opacity={open ? 1 : 0}
                                        transition="opacity .1s ease-in-out"
                                        justifyContent="start"
                                        whiteSpace={'unset'}
                                        flex={1}
                                        height="56px"
                                        variant="ghost"
                                        color={
                                            isActive
                                                ? currentColors.menuActiveTextColor
                                                : currentColors.menuTextColor
                                        }
                                        py="18px"
                                        pl="15px"
                                        pr="24px"
                                        textAlign="left"
                                        fontSize="15px"
                                        fontWeight={
                                            isActive ? 'bold' : 'normal'
                                        }
                                        // rightIcon={
                                        //     subMenu?.length ? (
                                        //         <ChevronDownIcon />
                                        //     ) : undefined
                                        // }
                                        _focus={{
                                            outline: 'none',
                                            boxShadow: 'none',
                                            border: '0px',
                                        }}
                                        _active={{
                                            backgroundColor: 'inherit',
                                            color: currentColors.primaryColor,
                                            outline: 'none',
                                            boxShadow: 'none',
                                            border: '0px',
                                        }}
                                        _hover={{
                                            backgroundColor: 'inherit',
                                            color: currentColors.menuActiveTextColor,
                                            outline: 'none',
                                            boxShadow: 'none',
                                            border: '0px',
                                        }}
                                        onClick={() => {
                                            ga.event({
                                                action: `${window.location.pathname.replaceAll(
                                                    '/',
                                                    ''
                                                )} > ${key} `,
                                            });
                                        }}
                                    >
                                        {path?.startsWith('http') ? (
                                            <a
                                                href={path}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                {t(`ui.mm.side-bar-${key}`)}
                                            </a>
                                        ) : (
                                            <Link to={path}>
                                                {t(`ui.mm.side-bar-${key}`)}
                                            </Link>
                                        )}
                                    </Button>

                                    {/* {subMenu ? (
                                            <Box
                                                pl="48px"
                                                display={
                                                    subMenuOpenStatus[index]
                                                        ? 'block'
                                                        : 'none'
                                                }>
                                                {subMenu.map(({ title }) => {
                                                    return <Box>{title}</Box>;
                                                })}
                                            </Box>
                                        ) : null} */}
                                </Flex>
                            );
                        })}
                        {hasTheme && (
                            <Flex
                                mx="22px"
                                mb="16px"
                                position="fixed"
                                bottom="20px"
                            >
                                <IconButton
                                    background="transparent"
                                    cursor="pointer"
                                    border="none"
                                    width="18px"
                                    height="18px"
                                    aria-label="theme"
                                    icon={
                                        colorMode === 'light'
                                            ? 'ThmDark'
                                            : 'ThmLight'
                                    }
                                    backgroundColor="transparent"
                                    onClick={() => {
                                        ga.event({
                                            action: `Menu > ${
                                                colorMode === 'light'
                                                    ? 'Light Mode'
                                                    : 'Dark Mode'
                                            } `,
                                            user: apiClient.session,
                                        });
                                        toggleColorMode();
                                    }}
                                    color={currentColors.menuTextColor}
                                    mr="8px"
                                    _focus={{
                                        outline: 'none',
                                        boxShadow: 'none',
                                        border: '0px',
                                    }}
                                    _hover={{
                                        backgroundColor: 'inherit',
                                        outline: 'none',
                                        boxShadow: 'none',
                                        border: '0px',
                                    }}
                                    _active={{
                                        backgroundColor: 'inherit',
                                        outline: 'none',
                                        boxShadow: 'none',
                                        border: '0px',
                                    }}
                                />
                                <Text
                                    color={currentColors.menuTextColor}
                                    fontSize="14px"
                                >
                                    {t('switch-to-color-mode', {
                                        color:
                                            colorMode === 'light'
                                                ? 'Dark'
                                                : 'Light',
                                    })}
                                </Text>
                            </Flex>
                        )}
                    </DrawerContent>
                </DrawerOverlay>
            </Drawer>
        );
    }

    return (
        <Flex
            position="fixed"
            right="0px"
            top="0px"
            left="0px"
            zIndex={1500}
            transition="width .1s ease-in-out"
            width={open ? '210px' : '80px'}
            height="100vh"
            bg={currentColors.menuBg}
            boxShadow={`0px -4px 4px ${currentColors.boxShadow}`}
            direction="column"
            onMouseOver={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
        >
            <Flex
                height="70px"
                width="100%"
                justifyContent="center"
                alignItems="center"
            >
                <Image
                    src={logo}
                    maxWidth="100%"
                    width="auto"
                    height={'35px'}
                    mx="auto"
                    mt="17.5px"
                    mb="17.5px"
                />
            </Flex>
            <Flex
                mt="20px"
                direction="column"
                width="100%"
            >
                {menu.map(({ title, subMenu, path, key }, index) => {
                    const isActive = pathname
                        ?.split('/')
                        ?.filter((item: any) => item)
                        .includes(
                            path?.split('/')?.filter((item: any) => item)[0]
                        );
                    const IconName = IconMap[key] || 'NavDash';

                    return (
                        <Flex
                            key={key}
                            alignItems="center"
                            height="56px"
                            justifyContent="flex-start"
                        >
                            {typeof IconName === 'string' ? (
                                <IconButton
                                    background="transparent"
                                    cursor="pointer"
                                    border="none"
                                    width="20px"
                                    height="20px"
                                    backgroundColor="transparent"
                                    color={
                                        isActive
                                            ? currentColors.primaryColor
                                            : currentColors.menuIconColor
                                    }
                                    icon={IconName}
                                    aria-label={key}
                                    ml="30px"
                                    _hover={{
                                        backgroundColor: 'inherit',
                                        color: currentColors.brand['300'],
                                        outline: 'none',
                                        boxShadow: 'none',
                                        border: '0px',
                                    }}
                                    _active={{
                                        backgroundColor: 'inherit',
                                        color: currentColors.primaryColor,
                                        outline: 'none',
                                        boxShadow: 'none',
                                        border: '0px',
                                    }}
                                    _focus={{
                                        backgroundColor: 'inherit',
                                        color: currentColors.primaryColor,
                                        outline: 'none',
                                        boxShadow: 'none',
                                        border: '0px',
                                    }}
                                ></IconButton>
                            ) : (
                                <ChakraIconButton
                                    backgroundColor="transparent"
                                    cursor="pointer"
                                    border="none"
                                    minWidth="20px"
                                    width="20px"
                                    height="20px"
                                    color={
                                        isActive
                                            ? currentColors.primaryColor
                                            : currentColors.menuIconColor
                                    }
                                    icon={IconName}
                                    aria-label={key}
                                    ml="30px"
                                    _hover={{
                                        backgroundColor: 'inherit',
                                        color: currentColors.brand['300'],
                                        outline: 'none',
                                        boxShadow: 'none',
                                        border: '0px',
                                    }}
                                    _active={{
                                        backgroundColor: 'inherit',
                                        color: currentColors.primaryColor,
                                        outline: 'none',
                                        boxShadow: 'none',
                                        border: '0px',
                                    }}
                                    _focus={{
                                        backgroundColor: 'inherit',
                                        color: currentColors.primaryColor,
                                        outline: 'none',
                                        boxShadow: 'none',
                                        border: '0px',
                                    }}
                                ></ChakraIconButton>
                            )}

                            {open ? (
                                <Button
                                    background="transparent"
                                    border="none"
                                    opacity={open ? 1 : 0}
                                    transition="opacity .2s ease-in-out"
                                    justifyContent="start"
                                    flex={1}
                                    height="56px"
                                    variant="ghost"
                                    whiteSpace={'unset'}
                                    color={
                                        isActive
                                            ? currentColors.menuActiveTextColor
                                            : currentColors.menuTextColor
                                    }
                                    py="18px"
                                    pl="15px"
                                    pr="24px"
                                    textAlign="left"
                                    fontSize="15px"
                                    fontWeight={isActive ? 'bold' : 'normal'}
                                    // rightIcon={
                                    //     subMenu?.length ? (
                                    //         <ChevronDownIcon />
                                    //     ) : undefined
                                    // }
                                    _focus={{
                                        outline: 'none',
                                        boxShadow: 'none',
                                        border: '0px',
                                    }}
                                    _active={{
                                        backgroundColor: 'inherit',
                                        color: currentColors.primaryColor,
                                        outline: 'none',
                                        boxShadow: 'none',
                                        border: '0px',
                                    }}
                                    onClick={() => {
                                        ga.event({
                                            action: `${window.location.pathname.replaceAll(
                                                '/',
                                                ''
                                            )} > ${key} `,
                                        });
                                    }}
                                    _hover={{
                                        backgroundColor: 'inherit',
                                        color: currentColors.menuActiveTextColor,
                                        outline: 'none',
                                        boxShadow: 'none',
                                        border: '0px',
                                    }}
                                >
                                    {path?.startsWith('http') ? (
                                        <a
                                            href={path}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {t(`ui.mm.side-bar-${key}`)}
                                        </a>
                                    ) : (
                                        <Link to={path}>
                                            {t(`ui.mm.side-bar-${key}`)}
                                        </Link>
                                    )}
                                </Button>
                            ) : null}
                            {/* {subMenu ? (
                                <Box
                                    pl="48px"
                                    display={
                                        subMenuOpenStatus[index]
                                            ? 'block'
                                            : 'none'
                                    }>
                                    {subMenu.map(({ title }) => {
                                        return <Box>{title}</Box>;
                                    })}
                                </Box>
                            ) : null} */}
                        </Flex>
                    );
                })}
            </Flex>
            {hasTheme && (
                <Flex
                    transition="opacity .2s ease-in-out"
                    mx="22px"
                    mb="16px"
                    position="fixed"
                    onClick={toggleColorMode}
                    bottom="20px"
                >
                    <IconButton
                        background="transparent"
                        cursor="pointer"
                        border="none"
                        width="18px"
                        height="18px"
                        aria-label="theme"
                        icon={colorMode === 'light' ? 'ThmDark' : 'ThmLight'}
                        backgroundColor="transparent"
                        color={currentColors.menuTextColor}
                        mr="8px"
                        _focus={{
                            outline: 'none',
                            boxShadow: 'none',
                            border: '0px',
                        }}
                        _hover={{
                            backgroundColor: 'inherit',
                            outline: 'none',
                            boxShadow: 'none',
                            border: '0px',
                        }}
                        _active={{
                            backgroundColor: 'inherit',
                            outline: 'none',
                            boxShadow: 'none',
                            border: '0px',
                        }}
                    />
                    {open ? (
                        <Text
                            color={currentColors.menuTextColor}
                            fontSize="14px"
                        >
                            {t('switch-to-color-mode', {
                                color: colorMode === 'light' ? 'Dark' : 'Light',
                            })}
                        </Text>
                    ) : null}
                </Flex>
            )}
        </Flex>
    );
};

Sider.propTypes = {
    t: PropTypes.func.isRequired,
};

export default withTranslation('translations')(Sider);
